import type { FormEvent, ReactNode } from "react"
import { Head, Link, router, useForm, usePage } from "@inertiajs/react"
import { AppShell } from "@/components/AppShell"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import type { PageProps } from "@/types/inertia"

type FieldDef = {
  readonly key: string
  readonly label: string
  readonly type: "text" | "number" | "select" | "date" | "boolean"
  readonly options?: string[]
}

type ContactFormRecord = {
  readonly id: number
  readonly email: string
  readonly first_name: string
  readonly last_name: string
  readonly company: string
  readonly title: string
  readonly phone: string
  readonly linkedin_url: string
  readonly notes: string
}

type ContactEditProps = {
  readonly contact: ContactFormRecord
  readonly field_definitions: readonly FieldDef[]
  readonly custom_fields: Record<string, unknown>
}

// Custom field values are scalar (text/number/select/date/boolean inputs) —
// typed as such so the form payload satisfies Inertia's FormDataConvertible.
type CustomFieldValue = string | number | boolean

type ContactFormData = {
  contact: {
    first_name: string
    last_name: string
    company: string
    title: string
    phone: string
    linkedin_url: string
    notes: string
  }
  custom_fields: Record<string, CustomFieldValue>
}

export default function ContactEdit({ contact, field_definitions, custom_fields }: ContactEditProps) {
  const { props } = usePage<PageProps<ContactEditProps>>()
  const errors = props.errors
  const title = `Edit ${contact.email}`
  const description = `Edit contact details for ${contact.email}.`
  const form = useForm<ContactFormData>({
    contact: {
      first_name: contact.first_name,
      last_name: contact.last_name,
      company: contact.company,
      title: contact.title,
      phone: contact.phone,
      linkedin_url: contact.linkedin_url,
      notes: contact.notes,
    },
    custom_fields: { ...custom_fields } as Record<string, CustomFieldValue>,
  })

  const submit = (event: FormEvent) => {
    event.preventDefault()
    router.patch(`/contacts/${contact.id}`, { contact: { ...form.data.contact, custom_fields: form.data.custom_fields } }, {
      preserveScroll: true,
    })
  }

  const updateCustomField = (key: string, value: CustomFieldValue) => {
    form.setData("custom_fields", { ...form.data.custom_fields, [key]: value })
  }

  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Head>
      <AppShell>
        <div className="flex flex-col gap-6">
          <header>
            <h1>Edit contact</h1>
            <p className="mt-2">{contact.email}</p>
          </header>

          {props.flash.notice && (
            <div className="rounded-md border border-accent bg-accent-faded px-4 py-3 text-sm text-accent-display">
              {props.flash.notice}
            </div>
          )}

          <form onSubmit={submit} className="space-y-6">
            <section className="rounded-lg border border-hairline bg-page p-6">
              <div className="grid gap-5 sm:grid-cols-2">
                <Field error={errors.first_name}>
                  <label htmlFor="first_name">First name</label>
                  <Input
                    id="first_name"
                    value={form.data.contact.first_name}
                    onChange={(event) =>
                      form.setData("contact", {
                        ...form.data.contact,
                        first_name: event.target.value,
                      })
                    }
                  />
                </Field>
                <Field error={errors.last_name}>
                  <label htmlFor="last_name">Last name</label>
                  <Input
                    id="last_name"
                    value={form.data.contact.last_name}
                    onChange={(event) =>
                      form.setData("contact", {
                        ...form.data.contact,
                        last_name: event.target.value,
                      })
                    }
                  />
                </Field>
                <Field error={errors.company}>
                  <label htmlFor="company">Company</label>
                  <Input
                    id="company"
                    value={form.data.contact.company}
                    onChange={(event) =>
                      form.setData("contact", {
                        ...form.data.contact,
                        company: event.target.value,
                      })
                    }
                  />
                </Field>
                <Field error={errors.title}>
                  <label htmlFor="title">Title</label>
                  <Input
                    id="title"
                    value={form.data.contact.title}
                    onChange={(event) =>
                      form.setData("contact", {
                        ...form.data.contact,
                        title: event.target.value,
                      })
                    }
                  />
                </Field>
                <Field error={errors.phone}>
                  <label htmlFor="phone">Phone</label>
                  <Input
                    id="phone"
                    value={form.data.contact.phone}
                    onChange={(event) =>
                      form.setData("contact", {
                        ...form.data.contact,
                        phone: event.target.value,
                      })
                    }
                  />
                </Field>
                <Field error={errors.linkedin_url}>
                  <label htmlFor="linkedin_url">LinkedIn URL</label>
                  <Input
                    id="linkedin_url"
                    value={form.data.contact.linkedin_url}
                    onChange={(event) =>
                      form.setData("contact", {
                        ...form.data.contact,
                        linkedin_url: event.target.value,
                      })
                    }
                  />
                </Field>
              </div>
              <Field error={errors.notes}>
                <label htmlFor="notes" className="mt-5 block">Notes</label>
                <textarea
                  id="notes"
                  className="form-control form-control-textarea min-h-32"
                  value={form.data.contact.notes}
                  onChange={(event) =>
                    form.setData("contact", {
                      ...form.data.contact,
                      notes: event.target.value,
                    })
                  }
                />
              </Field>
            </section>

            {field_definitions && field_definitions.length > 0 && (
              <section className="rounded-lg border border-hairline bg-page p-6">
                <h2>Custom fields</h2>
                <div className="mt-5 grid gap-5 sm:grid-cols-2">
                  {field_definitions.map((field) => (
                    <Field key={field.key}>
                      {field.type === "boolean" ? (
                        <div className="flex items-center gap-3 pt-1">
                          <Checkbox
                            id={`cf_${field.key}`}
                            checked={!!form.data.custom_fields[field.key]}
                            onChange={(event) =>
                              updateCustomField(field.key, event.target.checked)
                            }
                          />
                          <label htmlFor={`cf_${field.key}`} className="font-normal text-ink-body">
                            {field.label}
                          </label>
                        </div>
                      ) : (
                        <>
                          <label htmlFor={`cf_${field.key}`}>{field.label}</label>
                          {field.type === "select" ? (
                            <Select
                              id={`cf_${field.key}`}
                              value={String(form.data.custom_fields[field.key] || "")}
                              onChange={(event) =>
                                updateCustomField(field.key, event.target.value)
                              }
                            >
                              <option value="">—</option>
                              {(field.options || []).map((opt) => (
                                <option key={opt} value={opt}>{opt}</option>
                              ))}
                            </Select>
                          ) : (
                            <Input
                              id={`cf_${field.key}`}
                              type={field.type === "date" ? "date" : field.type === "number" ? "number" : "text"}
                              value={String(form.data.custom_fields[field.key] || "")}
                              onChange={(event) =>
                                updateCustomField(field.key, event.target.value)
                              }
                            />
                          )}
                        </>
                      )}
                    </Field>
                  ))}
                </div>
              </section>
            )}

            <div className="flex justify-end gap-3">
              <Button asChild variant="secondary">
                <Link href={`/contacts/${contact.id}`}>Cancel</Link>
              </Button>
              <Button type="submit" disabled={form.processing}>
                Save contact
              </Button>
            </div>
          </form>
        </div>
      </AppShell>
    </>
  )
}

function Field({
  children,
  error,
}: {
  readonly children: ReactNode
  readonly error?: string
}) {
  return (
    <div className="space-y-2">
      {children}
      {error && <div className="text-sm text-danger-display">{error}</div>}
    </div>
  )
}
